import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import PhotoGallery from "@/components/app/PhotoGallery";

/**
 * WorkItemSheet — full-screen slide-up sheet for editing / adding a work item.
 * Mobile-only. Traps focus, closes on ESC, confirms discard on dirty state.
 */
export default function WorkItemSheet({
    open,
    reportId,
    item, // { id?, title, location, description_of_work, materials_used, photos[] }
    onSave, // (nextItem, {addAnother}) => Promise
    onDelete, // ()
    onClose, // ()
    isNew = false,
}) {
    const [draft, setDraft] = useState(() => item || {});
    const [errors, setErrors] = useState({});
    const [shake, setShake] = useState({});
    const [dirty, setDirty] = useState(false);
    const titleRef = useRef(null);
    const sheetRef = useRef(null);

    useEffect(() => {
        if (open) {
            setDraft(item || {});
            setErrors({});
            setShake({});
            setDirty(false);
        }
    }, [open, item]);

    // Autofocus title on open
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            titleRef.current?.focus();
        }, 260);
        return () => clearTimeout(t);
    }, [open]);

    // Body scroll lock while sheet open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // ESC to close (with confirm if dirty)
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") requestClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dirty]);

    const setField = (k, v) => {
        setDraft((d) => ({ ...d, [k]: v }));
        setDirty(true);
    };

    const validate = () => {
        const e = {};
        if (!(draft.title || "").trim()) e.title = "Title is required";
        if (!(draft.description_of_work || "").trim())
            e.description_of_work = "Description is required";
        setErrors(e);
        if (Object.keys(e).length) {
            const s = {};
            Object.keys(e).forEach((k) => (s[k] = true));
            setShake(s);
            setTimeout(() => setShake({}), 260);
        }
        return Object.keys(e).length === 0;
    };

    const doSave = async (addAnother) => {
        if (!validate()) return;
        await onSave(draft, { addAnother });
        // Parent decides whether to close (Save closes, Save & Add stays with a fresh item)
    };

    const requestClose = () => {
        if (dirty) {
            if (window.confirm("Discard this work item?")) onClose();
        } else {
            onClose();
        }
    };

    const onPhotosChange = (nextPhotos) => {
        setDraft((d) => ({ ...d, photos: nextPhotos }));
    };

    if (!open) return null;

    const errClass = (k) =>
        `${shake[k] ? "field-shake" : ""}`;

    return (
        <>
            <div className="sheet-backdrop" onClick={requestClose} aria-hidden />
            <div
                ref={sheetRef}
                className="sheet"
                role="dialog"
                aria-modal="true"
                data-testid="work-item-sheet"
            >
                <div className="sheet-header">
                    <button
                        type="button"
                        onClick={requestClose}
                        className="btn btn-secondary btn-sm btn-icon"
                        aria-label="Cancel and close"
                        data-testid="sheet-close"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                    </button>
                    <h2>{isNew ? "New work item" : "Edit work item"}</h2>
                </div>

                <div className="sheet-body">
                    <div className={errClass("title")}>
                        <label className="label label-required" htmlFor="wi-sheet-title">
                            Title
                        </label>
                        <input
                            id="wi-sheet-title"
                            ref={titleRef}
                            className="input"
                            value={draft.title || ""}
                            onChange={(e) => setField("title", e.target.value)}
                            autoCapitalize="words"
                            data-testid="sheet-title"
                        />
                        {errors.title && (
                            <div className="err" data-testid="sheet-title-error">{errors.title}</div>
                        )}
                    </div>

                    <div className={`mt-4 ${errClass("description_of_work")}`}>
                        <label className="label label-required" htmlFor="wi-sheet-desc">
                            Description of work
                        </label>
                        <textarea
                            id="wi-sheet-desc"
                            rows={5}
                            className="textarea"
                            value={draft.description_of_work || ""}
                            onChange={(e) => setField("description_of_work", e.target.value)}
                            autoCapitalize="sentences"
                            data-testid="sheet-description"
                        />
                        {errors.description_of_work && (
                            <div className="err" data-testid="sheet-description-error">
                                {errors.description_of_work}
                            </div>
                        )}
                    </div>

                    <div className="mt-4">
                        <label className="label" htmlFor="wi-sheet-location">Location</label>
                        <input
                            id="wi-sheet-location"
                            className="input"
                            value={draft.location || ""}
                            onChange={(e) => setField("location", e.target.value)}
                            autoComplete="off"
                            data-testid="sheet-location"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="label" htmlFor="wi-sheet-mat">Materials used</label>
                        <textarea
                            id="wi-sheet-mat"
                            rows={3}
                            className="textarea"
                            value={draft.materials_used || ""}
                            onChange={(e) => setField("materials_used", e.target.value)}
                            autoCapitalize="sentences"
                            data-testid="sheet-materials"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="label">Photos</label>
                        <PhotoGallery
                            reportId={reportId}
                            photos={draft.photos || []}
                            workItemId={draft.id}
                            readOnly={!draft.id}
                            onChange={onPhotosChange}
                            testIdPrefix="sheet-photos"
                            mobile
                        />
                        {!draft.id && (
                            <div className="text-[11px] text-slate-400 mt-1">
                                Save this work item before adding photos.
                            </div>
                        )}
                    </div>
                </div>

                <div className="sheet-footer">
                    <div className="btn-row">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => doSave(false)}
                            data-testid="sheet-save"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => doSave(true)}
                            data-testid="sheet-save-and-add"
                        >
                            Save &amp; Add
                        </button>
                    </div>
                    {!isNew && onDelete && (
                        <button
                            type="button"
                            className="text-[13px] text-red-600 inline-flex items-center justify-center gap-1 py-1"
                            onClick={onDelete}
                            data-testid="sheet-delete"
                        >
                            <Trash2 size={13} strokeWidth={2} />
                            Delete
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
